
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { logSecurityEvent } from '@/utils/security';

interface UseAuthGuardOptions {
  redirectTo?: string;
  requireAuth?: boolean;
}

export const useAuthGuard = (options: UseAuthGuardOptions = {}) => {
  const { redirectTo = '/', requireAuth = true } = options;
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (requireAuth && !isAuthenticated) {
      logSecurityEvent('auth_guard_redirect', {
        from: location.pathname,
        to: redirectTo,
        reason: 'unauthenticated'
      });
      navigate(redirectTo, { replace: true, state: { from: location } });
    } else if (!requireAuth && isAuthenticated) {
      // Redirect authenticated users away from login pages
      const dashboardPath = '/dashboard';
      logSecurityEvent('auth_guard_redirect', {
        from: location.pathname,
        to: dashboardPath,
        reason: 'already_authenticated',
        userId: user?.id
      });
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location, redirectTo, requireAuth, user?.id]);

  return {
    isAuthenticated,
    isLoading,
    user,
    isAuthorized: requireAuth ? isAuthenticated : true
  };
};
